// Transform the data to Laravel API resource format
function transformData(data) {
    if (data.links && data.meta) {
        return data;
    }

    return {
        data: data.data,
        links: {
            first: data.first_page_url,
            last: data.last_page_url,
            next: data.next_page_url,
            prev: data.prev_page_url,
        },
        meta: {
            current_page: data.current_page,
            from: data.from,
            last_page: data.last_page,
            links: data.links,
            path: data.path,
            per_page: data.per_page,
            to: data.to,
            total: data.total,
        },
    };
}

// Normalize the URL
function normalizeUrl(url) {
    return url.startsWith('/') ? `${window.location.origin}${url}` : url;
}

// Filter out unwanted params from the URL
function filterPageUrl(url, defaultParams) {
    if (!url.includes('?')) return url;

    url = new URL(normalizeUrl(url));
    const searchParams = url.searchParams;
    searchParams.delete('_ajax');

    if (searchParams.get('page') == 1) {
        searchParams.delete('page');
    }

    for (const [key, value] of Object.entries(defaultParams)) {
        if (searchParams.get(key) == value) {
            searchParams.delete(key);
        }
    }

    return url.href;
}

// Generate Bootstrap 4 pagination links, based on Laravel pagination data
function updatePaginationLinks(root, links) {
    const previous = links[0];
    const next = links[links.length - 1];
    let desktopLinks = '';
    let mobileLinks = '';

    if (previous.url === null) {
        desktopLinks += `<li class="page-item disabled" aria-disabled="true" aria-label="${previous.label}"><span class="page-link" aria-hidden="true">&lsaquo;</span></li>`;
    } else {
        desktopLinks += `<li class="page-item"><a class="page-link" href="${previous.url}" rel="prev" aria-label="${previous.label}">&lsaquo;</a></li>`;
    }

    for (let i = 1; i < links.length - 1; i++) {
        const link = links[i];

        if (link.url === null) {
            desktopLinks += '<li class="page-item disabled" aria-disabled="true"><span class="page-link">...</span></li>';
            mobileLinks += '<option disabled>...</span>';
        } else {
            if (link.active === true) {
                desktopLinks += `<li class="page-item active" aria-current="page"><span class="page-link">${link.label}</span></li>`;
                mobileLinks += `<option selected>${link.label}</span>`;
            } else {
                desktopLinks += `<li class="page-item"><a class="page-link" href="${link.url}">${link.label}</a></li>`;
                mobileLinks += `<option value="${link.url}">${link.label}</span>`;
            }
        }
    }

    if (next.url === null) {
        desktopLinks += `<li class="page-item disabled" aria-disabled="true" aria-label="${next.label}"><span class="page-link" aria-hidden="true">&rsaquo;</span></li>`;
    } else {
        desktopLinks += `<li class="page-item"><a class="page-link" href="${next.url}" rel="prev" aria-label="${next.label}">&rsaquo;</a></li>`;
    }

    Alpine.mutateDom(() => {
        root.querySelectorAll('.pagination').forEach((element) => (element.innerHTML = desktopLinks));
        root.querySelectorAll('.pagination-mobile select').forEach((element) => (element.innerHTML = mobileLinks));

        for (const [key, link] of Object.entries({ previous, next })) {
            root.querySelectorAll(`.pagination-mobile .${key}-page`).forEach((element) => {
                element.dataset.url = link.url || '';
                element.classList[link.url === null ? 'add' : 'remove']('disabled');
            });
        }
    });
}

// ListPage component
export default function (...extenders) {
    const contentElement = document.getElementById('listpage-content');

    let root;
    let _currentUrl = '';
    let _popstateListener;
    let _isPopFromHistory = false;
    let _scrollPositions = {};
    let _dataPromise = null;

    const component = {
        url: null,
        data: {},
        params: {},
        defaultParams: {},

        // Initialization on page load
        init() {
            root = this.$root;

            // Transform data
            this.data = transformData(this.data);

            // Page URL
            this.url ??= window.location.href.split('?')[0];

            // Page parameters
            this.defaultParams ??= {};

            const params = Helper.getSearchParams(this.defaultParams);

            for (const [key, value] of Object.entries(params)) {
                if (typeof value !== 'object') continue;

                params[key] = value.value;
            }

            this.params = params;

            // Target item for highlight
            const itemId = parseInt(root.dataset.itemId, 10);
            const targetItem = this.data.data.find((element) => element.id === itemId);

            if (targetItem) {
                this.$nextTick(() => {
                    const row = document.getElementById('item-' + itemId);

                    if (!row) return;

                    this.highlightRow(row);

                    const elementPosY = row.getBoundingClientRect().top;

                    if (elementPosY > window.innerHeight - 120) {
                        Helper.scrollToElement(row, Math.max(200, Math.min(500, elementPosY / 4)), 160);
                    }
                });
            }

            // Hijack paginations click
            root.querySelectorAll('.pagination').forEach((element) => {
                ['touchstart', 'mousedown'].forEach((type) =>
                    element.addEventListener(type, (event) => {
                        event.preventDefault();

                        if (event.target.href) this.prepareDataPromise(event.target.href);
                    })
                );

                element.addEventListener('click', (event) => {
                    event.preventDefault();

                    if (event.target.href) this.load(event.target.href);
                });
            });

            // Attach getPaginationInfo()
            root.querySelectorAll('.pagination-info p').forEach((element) => element.setAttribute('x-html', 'getPaginationInfo()'));

            // Save current state
            _currentUrl = window.location.href;
            window.history.scrollRestoration = 'manual';

            window.history.replaceState(
                {
                    name: 'listpage',
                    timestamp: Date.now(),
                    url: _currentUrl,
                    data: JSON.parse(JSON.stringify(this.data)),
                    params: JSON.parse(JSON.stringify(this.params)),
                },
                '',
                _currentUrl
            );

            // Handle popstate event
            root.dataset.pathname = window.location.pathname;

            _popstateListener = (event) => {
                if (event.state?.name !== 'listpage' || root.dataset.pathname !== window.location.pathname) return;

                _isPopFromHistory = true;
                _scrollPositions[_currentUrl] = window.scrollY;

                if (Date.now() - event.state.timestamp > 15000) {
                    this.load(event.state.url);
                } else {
                    this.showSpinner();
                    window?.Bolt?.enabled && window?.Bolt?.startProgressBar();

                    window.setTimeout(() => {
                        this.refresh(event.state.data, event.state.params);

                        this.$nextTick(() => {
                            window?.Bolt?.stopProgressBar();
                            this.hideSpinner();
                        });
                    }, 100);
                }
            };

            if (window.Bolt) {
                Bolt.skipPopStateHandling((event) => event.state?.name === 'listpage' && root.dataset.pathname === window.location.pathname);
            }

            window.addEventListener('popstate', _popstateListener);

            // Run postInit, if available
            this.postInit?.();

            this.$nextTick(() => this.hideSpinner());
        },

        // Clean up
        destroy() {
            if (window.Bolt) {
                Bolt.skipPopStateHandling(() => false);
            }

            window.removeEventListener('popstate', _popstateListener);
        },

        // Load a page
        load(targetUrl, params) {
            const startTime = Date.now();

            if (_dataPromise === null) {
                this.prepareDataPromise(targetUrl, params);
            }

            this.showSpinner();
            window?.Bolt?.enabled && window?.Bolt?.startProgressBar();

            _dataPromise
                .then((data) => {
                    const duration = Date.now() - startTime;

                    window.setTimeout(
                        () => {
                            this.refresh(data);

                            // Run postLoad, if available
                            this.postLoad?.();

                            this.$nextTick(() => {
                                window?.Bolt?.stopProgressBar();
                                this.hideSpinner();
                            });
                        },
                        duration < 100 ? 100 - duration : 0
                    );

                    _dataPromise = null;
                })
                .catch((error) => {
                    if (Object.hasOwn(error, 'target')) {
                        error.target.searchParams.delete('_ajax');
                        window.location.href = error.target;
                    } else {
                        console.error(error);
                    }
                });
        },

        // Prepare data promise
        prepareDataPromise(targetUrl = this.url, params = Object.assign({}, this.params, { page: 1, _ajax: 1 })) {
            for (const [key, value] of Object.entries(this.defaultParams)) {
                if (typeof value === 'object') {
                    if (params[value.depends].length === 0) {
                        params[key] = '';
                    }
                } else {
                    if (String(params[key]) === String(value)) {
                        params[key] = '';
                    }
                }
            }

            const target = new URL(normalizeUrl(targetUrl));

            for (const [key, value] of Object.entries(params)) {
                if (!target.searchParams.has(key) && String(value).length > 0) {
                    target.searchParams.append(key, value);
                }
            }

            _dataPromise = (async () => {
                try {
                    return (await axios.get(target)).data;
                } catch (error) {
                    throw { error, target };
                }
            })();
        },

        // Refresh the rendering of the page
        refresh(data, params) {
            this.data = transformData(data);

            if (params) {
                this.params = params;
            } else {
                this.params.page = this.data.meta.current_page;
            }

            const activeLink = this.data.meta.links.find((link) => link.active === true);

            const targetUrl = filterPageUrl(activeLink.url, this.defaultParams);

            _scrollPositions[_currentUrl] = window.scrollY;
            window.history.scrollRestoration = 'manual';

            window.history[_isPopFromHistory ? 'replaceState' : 'pushState'](
                {
                    name: 'listpage',
                    timestamp: Date.now(),
                    url: targetUrl,
                    data: JSON.parse(JSON.stringify(this.data)),
                    params: JSON.parse(JSON.stringify(this.params)),
                },
                '',
                targetUrl
            );

            _currentUrl = targetUrl;

            // Preprocess links
            for (const link of this.data.meta.links) {
                if (typeof link.url !== 'string') continue;

                link.url = filterPageUrl(link.url, this.defaultParams);
            }

            updatePaginationLinks(root, this.data.meta.links);

            // Still undecided on scroll restoration
            // if (_isPopFromHistory) {
            //     this.$nextTick(() => {
            //         window.scrollTo({ top: _scrollPositions[_currentUrl] ?? 0 });
            //     });
            // }

            _isPopFromHistory = false;
        },

        // Show the spinner
        showSpinner(show = true) {
            contentElement?.classList?.[show ? 'add' : 'remove']('loading');
        },

        // Remove the spinner
        hideSpinner() {
            this.showSpinner(false);
        },

        // Clear the search string and reload the page, if necessary
        clearSearch(searchInput) {
            ['search', 'search_value'].forEach((param) => {
                if (!Object.hasOwn(this.params, param)) return;

                this.params[param] = '';

                if (new URL(window.location).searchParams.has(param)) {
                    this.load();
                }
            });

            searchInput.focus();
        },

        // Sort by the specified column
        sortBy(column) {
            const activeSorter = this.getActiveSorter();

            this.params.sort = column === activeSorter.column ? (activeSorter.down ? '' : '-') + activeSorter.column : '-' + column;
            this.load();
        },

        // Get sort icon for specified column
        getSortIcon(column) {
            const activeSorter = this.getActiveSorter();

            return column === activeSorter.column
                ? `<span class="fas fa-sort-${activeSorter.down ? 'down' : 'up'}"></span>`
                : '<span class="fas fa-sort" style="opacity: 0.4"></span>';
        },

        // Get current active sort column
        getActiveSorter() {
            const down = this.params.sort.startsWith('-');
            const column = this.params.sort.slice(down ? 1 : 0);

            return { column, down };
        },

        // Get pagination info
        getPaginationInfo() {
            const meta = this.data.meta;

            if (meta.total > 0) {
                if (meta.last_page === 1) {
                    const count = meta.to - meta.from + 1;

                    return trans_choice('[1] Showing :count record|[2,*] Showing :count records', count, {
                        count: count.toLocaleString(),
                    });
                } else {
                    return __('Showing :from to :to of :total records', {
                        from: meta.from.toLocaleString(),
                        to: meta.to.toLocaleString(),
                        total: meta.total.toLocaleString(),
                    });
                }
            } else {
                return __('No record');
            }
        },

        // Highlight the item's row
        highlightRow(row) {
            row.classList.add('table-success');
            row.style.transition = 'all 1.8s linear';

            window.setTimeout(() => {
                row.classList.remove('table-success');
                window.setTimeout(() => (row.style.transition = ''), 1800);
            }, 1800);
        },

        // Delete item
        deleteItem(item, callback) {
            Helper.showPageSpinner();

            return axios
                .delete(this.url + '/' + item.id, { params: Helper.getSearchParams() })
                .then((response) => {
                    this.refresh(response.data);

                    if (callback) {
                        callback(response.data);
                    }
                })
                .catch((error) => {
                    if (Object.hasOwn(error, 'response')) {
                        Helper.showNotificationError(error.response.data.message);
                    } else {
                        console.error(error);
                    }
                })
                .then(() => Helper.removePageSpinner());
        },
    };

    extenders.unshift(component);

    return Helper.mergeAlpineComponents.apply(Helper, extenders);
}
